import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box } from "@mui/material";
import { BlockEditor } from "./BlockEditor";
import type { ReportBlock } from "../api/types";

interface ReportCanvasProps {
  blocks: ReportBlock[];
  clientId: string | null;
  onChange: (blocks: ReportBlock[]) => void;
  /** Generates Director-targeted narrative; wired only to narrative blocks. */
  onGenerateNarrative?: () => Promise<string>;
  /** Generates an AI action plan; wired only to action-plan blocks. */
  onGenerateActionPlan?: () => Promise<string[]>;
}

/** One sortable row: wires dnd-kit's transform + drag handle into BlockEditor. */
const SortableBlock: React.FC<{
  block: ReportBlock;
  clientId: string | null;
  onChange: (block: ReportBlock) => void;
  onRemove: () => void;
  onGenerateNarrative?: () => Promise<string>;
  onGenerateActionPlan?: () => Promise<string[]>;
}> = ({
  block,
  clientId,
  onChange,
  onRemove,
  onGenerateNarrative,
  onGenerateActionPlan,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  return (
    <Box
      ref={setNodeRef}
      sx={{ opacity: isDragging ? 0.5 : 1 }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <BlockEditor
        block={block}
        clientId={clientId}
        onChange={onChange}
        onRemove={onRemove}
        dragHandleProps={{ ...attributes, ...listeners }}
        onGenerateNarrative={
          block.type === "narrative" ? onGenerateNarrative : undefined
        }
        onGenerateActionPlan={
          block.type === "actionPlan" ? onGenerateActionPlan : undefined
        }
      />
    </Box>
  );
};

/**
 * Drag-and-drop report canvas. Reorders blocks on drop and proxies per-block
 * edits/removals up to the parent, which owns the layout state.
 */
export const ReportCanvas: React.FC<ReportCanvasProps> = ({
  blocks,
  clientId,
  onChange,
  onGenerateNarrative,
  onGenerateActionPlan,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (id: string, next: ReportBlock) =>
    onChange(blocks.map((b) => (b.id === id ? next : b)));

  const removeBlock = (id: string) =>
    onChange(blocks.filter((b) => b.id !== id));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={blocks.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        {blocks.map((block) => (
          <SortableBlock
            key={block.id}
            block={block}
            clientId={clientId}
            onChange={(next) => updateBlock(block.id, next)}
            onRemove={() => removeBlock(block.id)}
            onGenerateNarrative={onGenerateNarrative}
            onGenerateActionPlan={onGenerateActionPlan}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
